/**
 * Help §5.7 — Generating documents (`document/GenerationStageView.tsx`,
 * `shared/src/contracts/doc-generation.ts`).
 */
import { helpSectionMeta } from '../help-sections.js';
import { Btn, Bullets, Callout, HelpSectionCard, Li, P, Shot, SubHead } from '../HelpPrimitives.js';

/** Each card the Generation stage produces, with what that document actually is. */
const DOCS = [
  { name: 'Kickoff Sheet', what: 'a full project briefing document.' },
  { name: 'Follow-up Email', what: 'a post-meeting email to the client.' },
  { name: 'Job Description', what: 'a LinkedIn-ready job description.' },
  { name: 'InMails', what: 'LinkedIn message templates for reaching candidates.' },
  { name: 'Interview Questions', what: 'questions for screening candidates.' },
  { name: 'Sourcing Sheet', what: 'a working sheet for the sourcing team.' },
] as const;

export function Documents(): React.JSX.Element {
  return (
    <HelpSectionCard meta={helpSectionMeta('documents')}>
      <SubHead>The documents the app creates</SubHead>
      <ul className="grid gap-2 sm:grid-cols-2">
        {DOCS.map((doc) => (
          <li
            key={doc.name}
            className="flex items-start gap-2.5 rounded-md border border-border bg-surface-muted px-3 py-2 text-sm"
          >
            <i className="fa-solid fa-file-lines mt-1 shrink-0 text-accent" aria-hidden />
            <span className="leading-relaxed">
              <span className="font-medium text-text-strong">{doc.name}</span>{' '}
              <span className="text-text-muted">— {doc.what}</span>
            </span>
          </li>
        ))}
      </ul>
      <P>
        Alongside them sits a separate <Btn>Kickoff done mail</Btn> block (formerly Internal
        Notification) — a short internal heads-up, sent on its own with the <Btn>Send</Btn> button.
        Once sent, the block shows <Btn>Sent</Btn> with the date and the button becomes{' '}
        <Btn>Send again</Btn>.
      </P>
      <Shot
        slug="generation"
        caption="The Generation stage — one card per document plus the Internal Notification block."
      />

      <SubHead>Generating</SubHead>
      <Bullets>
        <Li>
          Click <Btn>Generate All Docs</Btn> to write every document at once from everything
          collected so far.
        </Li>
        <Li>
          Anything typed into the <Btn>AI Instructions</Btn> field — tone, things to emphasise —
          applies to all of them.
        </Li>
      </Bullets>

      <SubHead>The buttons on each document card</SubHead>
      <P>
        <Btn>Generate</Btn>, <Btn>View / Edit</Btn>, <Btn>Regenerate</Btn>, <Btn>Approve</Btn>,{' '}
        <Btn>Download</Btn>, <Btn>Send</Btn> — and, on the Kickoff Sheet only,{' '}
        <Btn>Send to Sourcing Team</Btn>.
      </P>

      <Callout title="Moving on">
        <Btn>Confirm &amp; Move to Final</Btn> only becomes active once every card is approved.
      </Callout>
    </HelpSectionCard>
  );
}
